import React from 'react';
import PropTypes from 'prop-types';
import { PROGRESS_STATE, STEP_TYPE } from '../../utils/Constants';
import * as S from './ProgressBar.styles';
import { useStepper } from '../../hooks/useStepper';
import Step from './Step/Step.inner';

/**
 *
 * @param stepProgress Objeto del tipo clave, valor. Donde la clave se corresponde
 * con la propiedad key del objeto step especificado en el array steps del objeto de
 * configuración.
 * Ejemplo para un stepper de tres steps:
 *        {
 *        user: 1,
 *        account: 0.3,
 *        profile: 0.2,
 *        }
 * @param configuration Objeto de configuracion para el armado del stepper.
 * El objeto tiene la forma:
 *  {
 *    steps: [{
 *      key: string, único y requerido, permite identificar su progreso.
 *      label: string, requerido, etiqueta asociada el step.
 *      type: string, opcional, puede ser ['overall', 'progress']
 *      paths: [array de strings con los paths que corresponden al step]
 *      images: {
 *        active: string, requerido, especifica el src de la imagen,
 *        completed: string, requerido cuando type es igual a 'overall', especifica el src de la imagen,
 *        disabled: string, requerido, especifica el src de la imagen,
 *      },
 *    },...],
 *   global: {
 *        active: string, opcional, especifica el src de la imagen para el estado activo del step, se toma esta
 *        configuración si no existe la definición en el objeto step del array steps.
 *        completed: string, opcional, análogo al anterior punto.
 *        disabled: string, opcional, análogo al anterior punto.
 *    },
 * }
 * Ejemplo:
 *  const steeperBarConfig = {
 *  steps: [
 *    {
 *     key: 'user',
 *     label: 'Creá tu usuario',
 *     paths: ['/user']
 *     images: {
 *        active: IconMap.completed,
 *        disabled: IconMap.completed,
 *      },
 *    },
 *    {
 *      key: 'account',
 *      label: 'Configurá tu cuenta',
 *      paths: ['/account', '/account-something'],
 *      images: {
 *        active: IconMap.accountActive,
 *        disabled: IconMap.accountDisable,
 *      },
 *    },
 *    {
 *      key: 'profile',
 *      label: 'Completá tu perfil',
 *      paths: ['/profile', '/profile-something'],
 *      images: {
 *        active: IconMap.profileActive,
 *        disabled: IconMap.profileDisabled,
 *      },
 *    },
 *    {
 *      key: 'overall',
 *      type: 'overall',
 *      label: 'Empezá a vender',
 *      paths: ['/last-path'],
 *      images: {
 *        completed: IconMap.completed,
 *        disabled: IconMap.overall,
 *      },
 *    },
 *  ],
 *   global: {
 *     image: {
 *       completed: 'done_outline',
 *       iconSize: 32,
 *     },
 *   },
 * };
 * @return {JSX.Element}
 * @constructor
 */

const ProgressBar = ({ configuration, isLandscape, currentLocation }) => {
  const {
    steps,
    global: {
      image: { completed: completedImg, iconSize: iconSizeInPx },
    },
  } = configuration;

  let stepProgress = useStepper({ steps, currentLocation });

  const isOverallStepType = (isLast, type) =>
    isLast && type && type === STEP_TYPE.LAST;

  const buildImage = (progress, image) =>
    progress === PROGRESS_STATE.COMPLETED ? completedImg : image;

  const totalSteps = steps.length;

  return (
    <S.Wrapper>
      <S.ProgressBarDisplay isLandscape={isLandscape}>
        {steps.map((stepConfiguration, index, steps) => {
          const { key, type, label, image } = stepConfiguration;
          const progress = stepProgress[key] || PROGRESS_STATE.DEFAULT;

          const validType = isOverallStepType(index === steps.length - 1, type)
            ? STEP_TYPE.LAST
            : STEP_TYPE.PROGRESS;

          return (
            <Step
              key={key}
              image={buildImage(progress, image)}
              label={label}
              progress={progress}
              type={validType}
              isLandscape={isLandscape}
              iconSize={iconSizeInPx}
              totalSteps={totalSteps}
            />
          );
        })}
      </S.ProgressBarDisplay>
    </S.Wrapper>
  );
};

ProgressBar.propTypes = {
  configuration: PropTypes.shape({
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        paths: PropTypes.array.isRequired,
        image: PropTypes.string.isRequired,
      })
    ),
    global: PropTypes.shape({
      image: PropTypes.shape({
        completed: PropTypes.string.isRequired,
        iconSize: PropTypes.number,
      }),
    }),
  }).isRequired,
  currentLocation: PropTypes.string.isRequired,
};

export default ProgressBar;
